import React, { useEffect } from 'react';
import { AsyncStorage, Text, ActivityIndicator, View } from 'react-native';
import { ApplicationProvider, Layout, IconRegistry, Card } from '@ui-kitten/components';
import { EvaIconsPack } from '@ui-kitten/eva-icons';
import { mapping, dark as darkTheme, light as lightTheme } from '@eva-design/eva';
import { default as appTheme } from './constants/kitten-ui-custom-theme.json';
import { SafeAreaProvider } from 'react-native-safe-area-context';
import { NavigationContainer, DefaultTheme as NavDefaultTheme, DarkTheme as NavDarkTheme } from '@react-navigation/native';
import { DrawerNavigator } from './navigation/drawer.navigator';
import { ThemeContext } from './utils/theme-context';
import { ApolloProvider } from '@apollo/react-hooks';
import { client } from './data/api/client/client';
import { hydrateTokenFromLocalStorage, AUTH_TOKEN_STORAGE_KEY, resetAuthToken, removeAuthTokenInStorage, getAuthTokenFromStorage, USER_OBJ_STORAGE_KEY, hydrateUserObjectFromLocalStorage, getUserObjectFromStorage } from './data/api/client/authToken';
import useLinking from './navigation/useLinking';
import { SplashScreen, Linking } from 'expo';
import * as Font from 'expo-font';

const prefix = Linking.makeUrl('/');
const themes = {
    light: { ...lightTheme, ...appTheme },
    dark: { ...darkTheme, ...appTheme }
};
export const AuthContext = React.createContext<IAuthContext>(null);
const navigationThemes = {
    light: { ...NavDefaultTheme },
    dark: {
        dark: true,
        colors: {
            ...NavDarkTheme.colors,
            card: '#212b46',
        }
    }
}

// NavDarkTheme.colors.background = '#212b46'

if (__DEV__ === false){
    console.log('removing console.logs');
    var methods = ['log', 'debug', 'warn', 'info'];
    for (var i = 0; i < methods.length; i++){
        console[methods[i]] = function() {};
    }
}


export default function App({ navigation }) {
    const [theme, setTheme] = React.useState('light');
    const [initialState, setInitialState] = React.useState();
    const [isReady, setIsReady] = React.useState(false);
    const containerRef = React.useRef();
    const { getInitialState } = useLinking(containerRef, { prefixes: [prefix] });
    const toggleTheme = () => {
        const nextTheme = theme === 'light' ? 'dark' : 'light';
        setTheme(nextTheme);
    };
    const currentTheme = themes[theme];



    const [state, dispatch] = React.useReducer((prevState: any, action: { type: string; token?: string; user: CurrentUser }) => {
        console.log('const [state, dispatch] = React.useReducer() has fired', { prevState, action });
        switch (action.type) {
            case 'RESTORE_TOKEN':
                return {
                    ...prevState,
                    isLoading: false,
                    userToken: action.token,
                    user: { ...action.user }
                };
            case 'SIGN_IN':
                return {
                    ...prevState,
                    isSignout: false,
                    userToken: action.token,
                    user: { ...action.user }
                };
            case 'SIGN_OUT':
                return {
                    ...prevState,
                    isSignout: true,
                    userToken: null,
                };
        }
        },
        {
            isLoading: true,    // We set this to true when we're trying to check if we already have a token saved in AsyncStorage
            isSignout: false,   // We set this to true when user is signing out, otherwise set it to false 
            userToken: null,    // The token for the user. If it's non-null, we assume the user is logged in, otherwise not.
            currentUser: null
        }
    );



    React.useEffect(() => {
        // Fetch the token from storage then navigate to our appropriate place
        const bootstrapAsync = async () => {
            let userToken = undefined;
            let userObject = undefined;

            try {
                // load fonts
                await Font.loadAsync({ 'fascinate-inline': require('./assets/fonts/FascinateInline-Regular.ttf') });

                // load token and user obj from storage
                userToken = await AsyncStorage.getItem('poall.' + AUTH_TOKEN_STORAGE_KEY);
                const userObjectAsString = await AsyncStorage.getItem('poall.' + USER_OBJ_STORAGE_KEY);
                const tokenHydrationSuccessful: boolean = await hydrateTokenFromLocalStorage(userToken);
                const userHydrationSuccessful: boolean = await hydrateUserObjectFromLocalStorage(userObjectAsString);
                if (userHydrationSuccessful) {
                    userObject = await getUserObjectFromStorage();
                }
                console.log('client token hydration successful = ', { tokenHydrationSuccessful, userToken, userHydrationSuccessful, userObject });
            }
            catch (error) {
                // Restoring token failed
            }


            getInitialState().catch(() => { }).then(state => {
                if (state !== undefined) {
                    setInitialState(state);
                }
                logAsyncStorage();
                setIsReady(true);
            });
            // After restoring token, we may need to validate it in production apps

            // This will switch to the App screen or Auth screen and this loading
            // screen will be unmounted and thrown away.
            dispatch({ type: 'RESTORE_TOKEN', token: userToken, user: userObject });
        };

        bootstrapAsync();
    }, [getInitialState]);


    
    const authContext = React.useMemo(() => ({
        signIn: async (token: string, user: CurrentUser) => {
            console.log('Auth Context: signIn: async (token) => {} has fired', {token, user});
            // In a production app, we need to send some data (usually username, password) to server and get a token
            // We will also need to handle errors if sign in failed
            // After getting token, we need to persist the token using `AsyncStorage`
            // In the example, we'll use a dummy token

            dispatch({ type: 'SIGN_IN', token: token, user: user });
        },
        signOut: () => {
            console.log('signOut: () => {} has fired');
            // In a production app, we need to clean up data and remove token from local storage
            // removeAuthTokenInStorage(); // remove token from local storage
            // resetAuthToken();   // set the local value of authToken to null
            dispatch({ type: 'SIGN_OUT', user: undefined });
        },
        signUp: async (data) => {
            console.log('signUp: async (data) => {} has fired', data);
            // In a production app, we need to send user data to server and get a token
            // We will also need to handle errors if sign up failed
            // After getting token, we need to persist the token using `AsyncStorage`
            // In the example, we'll use a dummy token

            dispatch({ type: 'SIGN_IN', token: 'dummy-auth-token', user: undefined });
        },
    }),[]);

    console.log('state.userToken = ' + state.userToken);
    console.log('initialState={initialState} = ', initialState);

    if (isReady === false) {
        return (
            <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
                <ActivityIndicator size='large' />
            </View>
        );
    }
    const authContextObject: IAuthContext = {...authContext, ...state };

    return (
        <AuthContext.Provider value={authContextObject}>
            <ApolloProvider client={client}>
                <IconRegistry icons={EvaIconsPack} />
                <ThemeContext.Provider value={{ theme, toggleTheme }}>
                    <ApplicationProvider mapping={mapping} theme={currentTheme}>
                        <SafeAreaProvider>
                                <NavigationContainer ref={containerRef} initialState={initialState} theme={theme === 'dark' ? navigationThemes.dark : navigationThemes.light}>
                                    <DrawerNavigator userAuthorized={!(state.userToken == null)} user={state.user} />
                                </NavigationContainer>
                        </SafeAreaProvider>
                    </ApplicationProvider>
                </ThemeContext.Provider>
            </ApolloProvider>
        </AuthContext.Provider>
    );
}




function logAsyncStorage() {
    if (!__DEV__) {
        return;
    }
    
    AsyncStorage.getAllKeys((err, keys) => {
        AsyncStorage.multiGet(keys, (error, stores) => {
            console.log('==== GETTING ALL STORED DATA BEFORE ANY APP OPS ====');
            stores.map((result, i, store) => {
                console.log({ [store[i][0]]: store[i][1] });
                return true;
            });
        });
    });
}




export interface IAuthContext {
    signIn: (data: { jwt_token: string, user: CurrentUser }) => Promise<void>;
    signOut: () => Promise<void>;
    signUp: (data) => Promise<void>;
    user: CurrentUser;
}

export interface CurrentUser {
    id: string;
    org_id: string;
    product_plan: ProductPlan;
    username: string;
    email: string;
    updated_at: string;
    created_at: string;
    avatar: string;
}

export interface ProductPlan {
    id: number;
    title: string;
    price: number;
}